"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SearchLoadingExperience } from "@/shared/components/SearchLoadingExperience";
import { getCampaign } from "@/containers/website/campaign/campaign-registry";

interface HotelResultsLoadingOverlayProps {
  cityName: string;
  searchParams: SearchParams;
  isB2BUser?: boolean;
}

export default function HotelResultsLoadingOverlay({
  cityName,
  searchParams,
  isB2BUser,
}: HotelResultsLoadingOverlayProps) {
  const [isVisible, setIsVisible] = useState(true);
  const [isComplete, setIsComplete] = useState(false);
  const [progress, setProgress] = useState(15);
  const hideTimerRef = useRef<number | null>(null);
  const isCompleteRef = useRef(false);
  const destination = useMemo(
    () => getSearchDestination(searchParams, cityName),
    [cityName, searchParams],
  );
  const campaign = getCampaign("partner");
  const campaignHref = campaign ? `/c/${campaign.slug}` : undefined;

  const startLoading = useCallback(() => {
    isCompleteRef.current = false;
    if (hideTimerRef.current) {
      window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    setIsVisible(true);
    setIsComplete(false);
    setProgress(15);
  }, []);

  const completeLoading = useCallback(() => {
    if (isCompleteRef.current) return;

    isCompleteRef.current = true;
    setIsComplete(true);
    setProgress(100);
    hideTimerRef.current = window.setTimeout(() => setIsVisible(false), 450);
  }, []);

  useEffect(() => {
    if (isComplete) return;

    const startedAt = Date.now();
    const interval = window.setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const nextProgress = 15 + Math.min(elapsed / 5000, 1) * 70;

      setProgress((current) => Math.max(current, Math.min(nextProgress, 85)));
    }, 80);

    return () => window.clearInterval(interval);
  }, [isComplete]);

  useEffect(() => {
    if (!isVisible || isComplete) return;

    const interval = window.setInterval(() => {
      const hasLoadingSkeleton = Boolean(
        document.querySelector("[data-hotel-search-loading='true']"),
      );

      if (!hasLoadingSkeleton) completeLoading();
    }, 180);

    return () => window.clearInterval(interval);
  }, [completeLoading, isComplete, isVisible]);

  useEffect(() => {
    const hasLoadingSkeleton = () =>
      Boolean(document.querySelector("[data-hotel-search-loading='true']"));

    if (!hasLoadingSkeleton()) {
      window.setTimeout(completeLoading, 0);
      return;
    }

    const observer = new MutationObserver(() => {
      if (!hasLoadingSkeleton()) completeLoading();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [completeLoading]);

  useEffect(() => {
    window.addEventListener("hotel-search-loading:start", startLoading);

    return () => {
      window.removeEventListener("hotel-search-loading:start", startLoading);
      if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);
    };
  }, [startLoading]);

  if (!isVisible) return null;

  return (
    <SearchLoadingExperience
      destination={destination}
      isComplete={isComplete}
      progress={progress}
      campaignHref={campaignHref}
      campaignLabel={campaign?.title}
      isB2BUser={isB2BUser}
    />
  );
}

function getSearchDestination(searchParams: SearchParams, fallback: string) {
  const params = searchParams as SearchParams & { name?: string };

  const queryName = params.name?.trim();

  if (queryName && !isLikelyCityCode(queryName)) return queryName;

  return fallback;
}

function isLikelyCityCode(value: string) {
  return /^[a-z]{3}$/i.test(value.trim());
}
